import React, { useState } from "react";
import { ActionIcon, Box, Text, UnstyledButton } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { ReportMetricDef } from "../../../../types/reportsV2";
import MetricTooltip, { hasMetricHelp } from "../../v2/MetricTooltip";
import styles from "./MobileCards.module.css";

/** Rows a card shows before "Show all", so many columns stay readable. */
export const VISIBLE_METRIC_ROWS = 5;

interface MetricRowProps {
  metric: ReportMetricDef;
  value: React.ReactNode;
}

/** Label · info · value. The info button reveals the definition's
 *  description and formula inline (tooltips don't suit touch). */
function MetricRow({ metric, value }: MetricRowProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <Box className={styles.metricRow}>
      <Box className={styles.metricLine}>
        <Text component="span" className={styles.metricLabel}>
          {metric.label}
        </Text>
        {hasMetricHelp(metric) && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            className={styles.infoButton}
            aria-label={`About ${metric.label}`}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((o) => !o)}
          >
            <IconInfoCircle size={14} aria-hidden />
          </ActionIcon>
        )}
        <Text component="span" className={styles.metricValue}>
          {value ?? "—"}
        </Text>
      </Box>
      {infoOpen && (
        <Box className={styles.metricInfo}>
          <MetricTooltip metric={metric} />
        </Box>
      )}
    </Box>
  );
}

export interface MetricRowsProps {
  /** Visible columns to list (the card header already shows one). */
  columns: ReportMetricDef[];
  cells: Record<string, React.ReactNode>;
}

/** A card's label / value rows: the first few, then "Show all". */
export default function MetricRows({ columns, cells }: MetricRowsProps) {
  const [showAll, setShowAll] = useState(false);
  if (columns.length === 0) return null;
  const shown = showAll ? columns : columns.slice(0, VISIBLE_METRIC_ROWS);
  return (
    <Box className={styles.metrics}>
      {shown.map((metric) => (
        <MetricRow key={metric.key} metric={metric} value={cells[metric.key]} />
      ))}
      {columns.length > VISIBLE_METRIC_ROWS && (
        <UnstyledButton
          type="button"
          className={styles.showAll}
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "Show fewer" : "Show all"}
        </UnstyledButton>
      )}
    </Box>
  );
}
